import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useFavicon } from 'ahooks'

import store from '../../store'
import { NETWORK_LIST, getChainIdFromShortName } from '../../utils/config'
import { eventBus } from '../../utils/tool'
import { connectWallet, disconnectWallet, discoverWalletProviders, getWalletSnapshot, restoreWallet, subscribeWallet } from '../../utils/walletProviders'

const shortAddress = (address) => address ? `${address.slice(0, 6)}...${address.slice(-4)}` : ''
const EMPTY_WALLET = { account: '', chainId: null, provider: null, walletId: '', walletName: '', wallets: [], status: 'idle', error: '' }

function targetChainIdFromLocation() {
    if (typeof window === 'undefined') return 0
    const shortName = window.location.hash.replace(/^#\/?/, '').split('/')[0]
    return getChainIdFromShortName(shortName)
}

function walletErrorMessage(error, t) {
    if (error?.code === 4001 || /rejected|denied/i.test(error?.message || '')) return t('您已取消钱包操作')
    return error?.message || t('钱包连接失败')
}

export default function ConsoleHeader() {
    const { t, i18n } = useTranslation()
    const miningInfo = useSelector((state) => state.miningInfo)
    const [wallet, setWallet] = useState(() => getWalletSnapshot() || EMPTY_WALLET)
    const [walletMenuOpen, setWalletMenuOpen] = useState(false)
    const [connectingId, setConnectingId] = useState('')
    const [networkLogoFailed, setNetworkLogoFailed] = useState(false)
    const menuRef = useRef(null)
    const projectLogo = miningInfo?.logo || miningInfo?.stakedTokenLogo || ''
    const projectName = miningInfo?.project_name || miningInfo?.stakedTokenName || t('质押挖矿')
    const assetSymbol = miningInfo?.stakedTokenSymbol || miningInfo?.symbol || t('质押控制台')
    useFavicon(projectLogo || '/img/favicon.ico')
    const targetChainId = targetChainIdFromLocation()
    const targetNetwork = useMemo(() => NETWORK_LIST.find((network) => Number(network.chainId) === Number(targetChainId)), [targetChainId])
    const walletNetwork = useMemo(() => NETWORK_LIST.find((network) => Number(network.chainId) === Number(wallet.chainId)), [wallet.chainId])
    const networkMatches = Boolean(wallet.account && (!targetChainId || Number(wallet.chainId) === Number(targetChainId)))
    const targetNetworkName = targetNetwork ? `${targetNetwork.name}${targetNetwork.isTestNetwork ? ` ${t('测试网')}` : ''}` : t('选择网络')
    const targetNetworkLogo = ['bsc', 'bsc-test'].includes(targetNetwork?.shortname) ? '/img/binance.png' : targetNetwork?.logo

    useEffect(() => setNetworkLogoFailed(false), [targetNetworkLogo])
    useEffect(() => {
        document.title = projectName
        const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]')
        if (appleTouchIcon) appleTouchIcon.setAttribute('href', projectLogo || '/img/favicon.ico')
    }, [projectLogo, projectName])
    useEffect(() => {
        const unsubscribe = subscribeWallet(setWallet)
        restoreWallet()
        return unsubscribe
    }, [])
    useEffect(() => {
        const payload = { account: wallet.account || '', chainId: wallet.chainId || null, provider: wallet.provider || null, walletName: wallet.walletName || '' }
        store.dispatch({ type: 'setAccount', value: payload.account })
        store.dispatch({ type: 'setChainId', value: payload.chainId })
        eventBus.emit('walletConnect', payload)
        eventBus.emit('walletChanged', payload)
    }, [wallet.account, wallet.chainId, wallet.provider, wallet.walletName])
    useEffect(() => {
        if (!walletMenuOpen) return undefined
        const closeMenu = (event) => { if (!menuRef.current?.contains(event.target)) setWalletMenuOpen(false) }
        document.addEventListener('pointerdown', closeMenu)
        return () => document.removeEventListener('pointerdown', closeMenu)
    }, [walletMenuOpen])

    const toggleWalletMenu = async () => {
        if (!walletMenuOpen) await discoverWalletProviders()
        setWalletMenuOpen((open) => !open)
    }
    const selectWallet = async (walletId) => {
        setConnectingId(walletId)
        try {
            await connectWallet(walletId)
            setWalletMenuOpen(false)
        } catch (error) {
            setWallet((current) => ({ ...current, error: walletErrorMessage(error, t) }))
        } finally {
            setConnectingId('')
        }
    }
    const changeLanguage = (language) => {
        i18n.changeLanguage(language)
        window.localStorage.setItem('i18nextLng', language)
    }

    return (
        <header className="console-header">
            <div className="console-header__brand" aria-label={projectName}>
                <span className="console-header__brand-mark">{projectLogo ? <img src={projectLogo} alt="" /> : <i className="fa fa-cube" />}</span>
                <span><strong>{projectName}</strong><small>{assetSymbol}</small></span>
            </div>
            <div className="console-header__actions">
                <div className={`network-chip ${networkMatches ? 'is-connected' : wallet.account ? 'is-warning' : ''}`}>
                    {targetNetworkLogo && !networkLogoFailed ? <img src={targetNetworkLogo} alt="" onError={() => setNetworkLogoFailed(true)} /> : <span className="network-chip__fallback"><i className="fa fa-network-wired" /></span>}
                    <span>{targetNetworkName}</span><span className="network-chip__status" aria-hidden="true" /><small>{networkMatches ? t('已连接') : wallet.account ? t('网络不匹配') : t('未连接')}</small>
                </div>
                <div className="language-switch" aria-label={t('选择语言')}><button type="button" className={i18n.resolvedLanguage === 'en' ? 'is-active' : ''} onClick={() => changeLanguage('en')}>EN</button><span>/</span><button type="button" className={i18n.resolvedLanguage === 'zh' ? 'is-active' : ''} onClick={() => changeLanguage('zh')}>中文</button></div>
                <div className="wallet-menu" ref={menuRef}>
                    <button type="button" className={`wallet-trigger ${wallet.account ? 'is-connected' : ''}`} onClick={toggleWalletMenu} aria-haspopup="dialog" aria-expanded={walletMenuOpen}><i className="fa fa-wallet" /><span className={wallet.account ? 'is-address' : ''}>{wallet.account ? shortAddress(wallet.account) : t('连接钱包')}</span><i className={`fa fa-chevron-${walletMenuOpen ? 'up' : 'down'}`} /></button>
                    {walletMenuOpen && <div className="wallet-popover" role="dialog" aria-label={t('选择钱包')}>
                        <div className="wallet-popover__heading"><span>{t('选择钱包')}</span>{wallet.account && <small>{wallet.walletName} · {walletNetwork?.name || wallet.chainId}</small>}</div>
                        <div className="wallet-popover__list">
                            {wallet.wallets.map((item) => <button type="button" key={item.id} className={wallet.walletId === item.id ? 'is-selected' : ''} onClick={() => selectWallet(item.id)} disabled={Boolean(connectingId)}>{item.icon ? <img src={item.icon} alt="" /> : <span className="wallet-popover__fallback"><i className="fa fa-wallet" /></span>}<span>{item.name}</span>{connectingId === item.id && <i className="fa fa-spinner loading-css" />}{wallet.walletId === item.id && wallet.account && connectingId !== item.id && <i className="fa fa-check-circle" />}</button>)}
                            {wallet.wallets.length === 0 && <div className="wallet-popover__empty"><i className="fa fa-exclamation-circle" /><span>{t('未检测到浏览器钱包')}</span></div>}
                        </div>
                        {wallet.error && <div className="wallet-popover__error">{wallet.error}</div>}
                        {wallet.account && <button type="button" className="wallet-popover__disconnect" onClick={() => { disconnectWallet(); setWalletMenuOpen(false) }}><i className="fa fa-sign-out-alt" /> {t('断开连接')}</button>}
                    </div>}
                </div>
            </div>
        </header>
    )
}
